import { Injectable } from '@angular/core';
import { useStore } from 'ngx-unificator/store';
import { SleighDataItem, Xmas, XmasPage, XmasStoreEntity } from '../../../core/store/xmas.store';
import { GlobalStoreName } from '../../../core/store';
import { BasePromo, EventType } from '../base-promo';
import { from, Observable, of, switchMap } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { GameListConfig } from '../../../core/shared/components/game-list/game-list.component';
import { GameCategory } from '../../../core/services/games/game-category';
import { ITournament } from '../../tournaments/tournaments.interface';

const BONUS_IDS: string[] = ['promo_spins_1_2025', 'promo_spins_2_2025', 'promo_spins_3_2025'];
export const DAILY_BONUS_MISSION = 'ID915';

export enum XmasAction {
  GO_TO_DEPOSIT = 'deposit',
  TOURNAMENTS = 'tournaments',
  PROMO_AUTH = 'promo-auth',
  GET_BONUS = 'get-bonus',
  GO_TO_BONUSES = 'bonuses',
}

@Injectable({
  providedIn: 'root',
})
export class XmasService extends BasePromo {

  public xmasStore = useStore<XmasStoreEntity>(GlobalStoreName.XMAS_STORE);

  public eventName = 'xmas';

  /**
   * Game list config
   */
  public gamesListConfig: GameListConfig = {
    filter: {
      category: GameCategory.XMAS_GAMES,
      limit: 5,
    },
    link: '/games/winter-holiday',
    title: '',
    hideTitle: true,
  };

  /**
   * Action rotes config
   */
  public actionRoutesConfig = {
    [XmasAction.GET_BONUS]: {
      key: null,
      url: '/account',
      authUrl: ['account'],
    },
    [XmasAction.GO_TO_BONUSES]: {
      key: null,
      url: '/bonuses',
      authUrl: ['bonuses'],
    },
    [XmasAction.TOURNAMENTS]: {
      key: 'promo-tournaments',
      url: '/tournaments',
      authUrl: ['tournaments'],
    },
    [XmasAction.GO_TO_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/account/deposit',
      authUrl: ['account', 'deposit'],
    },
    [XmasAction.PROMO_AUTH]: {
      key: 'promo-auth',
      url: '/promo/xmas',
      authUrl: null,
    },
  };

  /**
   * Tournaments list
   */
  public tournaments$: Observable<ITournament[]> = this.getTournaments$('xmas');

  get store(): Xmas {
    return this.xmasStore.storeSignal();
  }

  get isDailyMissionCompleted(): boolean {
    return this.group.isExistGroup(DAILY_BONUS_MISSION);
  }

  public initPromo$() {
    this._patchXmasStore({ isLoadedPromo: false });
    this.user.auth$.pipe(
      switchMap(() => this.fetchPromoData$()),
      filter(data => !!data[0]),
      map(([promo, lootbox, fs, bonuses]) => this._processPromoData$(promo, lootbox, fs, bonuses)),
      map(([promo, lootbox, fs, bonuses]) => this._processAdditionalPromoData$(promo, lootbox, fs, bonuses)),
      tap(([promo, lootboxes, freespins, bonuses]) => {
        this._updatePageStore(promo);
        this.updateCurrentBonus(lootboxes, freespins, bonuses, BONUS_IDS, this.xmasStore);
        this.updateParticipateInPromo(lootboxes, BONUS_IDS, this.xmasStore);
        this._patchXmasStore({ isLoadedPromo: true });
      }),
      filter(() => this.platform.isBrowser),
      switchMap(() => from(this.initWelcomePromoModal())),
    ).subscribe();
  };

  public onSpinBonus(isSpinBonus: boolean) {
    this._patchXmasStore({ isSpinBonus });
  }

  public onActivateBonus() {
    this.onActivateBonus$(this.xmasStore).subscribe(() => {
      this.onSpinBonus(false);
    });
  }

  public onClearBonus() {
    this._patchXmasStore({ currentPrize: null, currentBonus: null });
  }

  private _updatePageStore(promo: XmasPage) {
    if (this.isPromoError(promo)) return;

    if (promo.isDisabledPromo) {
      console.log(promo);
      this._patchXmasStore({ page: promo });
      return;
    }

    const page = {
      ...promo,
      HowItWorksSteps: [...Object.values(promo?.HowWorksSteps)],
      unpublishAt: promo?.unpublishAt,
    };

    this._patchXmasStore({
      page,
      isLoadedPromo: true,
      sleighData: page?.SleighData,
      currentSleighItem: page?.SleighData.find(item => item.currentDayIndex),
      currentSleighItemIndex: page?.SleighData.findIndex(item => item.currentDayIndex),
    });
  }

  private _processPromoData$(promo: XmasPage[], lootbox: any[], fs: any[], bonuses: any[]): any {
    return [this._resolveByRangeMonthDate(promo), lootbox, fs, bonuses];
  };

  private _processAdditionalPromoData$(promo: XmasPage[], lootbox: any[], fs: any[], bonuses: any[]): any {
    return [promo?.map(promoItem => ({
      ...promoItem,
      SleighData: this._resolveSleigh(promoItem?.SleighData, fs, lootbox),
      HowWorksSteps: [...Object.values(promoItem?.HowWorksSteps)],
      unpublishAt: this.time.toLocalDate(promoItem?.unpublishAt),
      publishAt: this.time.toLocalDate(promoItem?.publishAt),
    }))[0],
      lootbox,
      fs,
      bonuses,
    ];
  };

  private _resolveByRangeMonthDate(promo: XmasPage[]): XmasPage[] {
    const startDate = new Date(promo?.[0]?.StartPromoDate);
    const monthDates = this._getRangeOfMonthDates(startDate);

    return [
      {
        ...promo?.[0],
        SleighData: monthDates.map((date, i) => ({
          date,
          short_date: this._formatShortDate(date),
        })),
      },
    ];
  }

  private _resolveSleigh(sleigh: SleighDataItem[], fs: any[], lootbox: any[]): any[] {

    return sleigh.map((sleighItem: SleighDataItem, i: number) => {

      const date = new Date(sleighItem?.date);
      const formattedDate = date?.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
      }).replace(/\//g, '.');

      return {
        ...sleighItem,
        locked: this._resolveLockedState(sleighItem?.date),
        currentDayIndex: this._currentIndexByCurrentDay(sleigh) === i,
        date: formattedDate,
      };
    });
  }

  private _resolveLockedState(date: string): boolean {
    const currentDate = new Date();
    const currentDateUTC = currentDate.toISOString();
    const originalDate = new Date(currentDateUTC);
    const convertedDateStr =
      originalDate.toISOString().split('T')[0] + ' 00:00:00.000000';
    return this.user.auth ? date > convertedDateStr : false;
  }

  private _currentIndexByCurrentDay(list: any[]): number {
    if (list) {
      const todayUTC = new Date().toISOString().split('T')[0];
      return list.findIndex(item => item.date.startsWith(todayUTC));
    }
  }

  private _formatShortDate(inputDate: any) {
    const date = new Date(inputDate);
    const monthNames = [
      't.jan', 't.feb', 't.mar', 't.apr', 't.may', 't.jun',
      't.jul', 't.aug', 't.sep', 't.oct', 't.nov', 't.dec'];

    const day = date.getDate().toString().padStart(2, '0');
    const month = monthNames[date.getMonth()];
    return `${day} ${this.translate.translate(month)}`;
  }

  private _getRangeOfMonthDates(startDate: Date): string[] {
    const year = startDate.getUTCFullYear();
    const month = startDate.getUTCMonth();

    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const dates: string[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
      const formattedDate = date.toISOString().split('T')[0] + ' 00:00:00.000000';
      dates.push(formattedDate);
    }

    return dates;
  }

  private _patchXmasStore(patch: Partial<XmasStoreEntity>) {
    this.xmasStore.storeSignal.update(store => ({ ...store, ...patch }));
  }

  public openHuntModal?(type: EventType): void {
    throw new Error('Method not implemented.');
  }

  async checkActivatedBonuses() {
    if (this.store.isWelcomeModalOpened) {
      return of(true);
    }

    if (!this.store.isUserAlreadyActivatedPromoLootbox && this.store.isLoadedPromo && !this.store.page.isDisabledPromo) {
      this._patchXmasStore({ isWelcomeModalOpened: true });
      const { XmasWelcomeModalComponent } = await import('../xmas/xmas-welcome-modal/xmas-welcome-modal.component');
      await this.modals.openLazy(XmasWelcomeModalComponent, { template: 'CLEAR' });
      const now = new Date();
      const day = now.getDay();
      const diff = 7 - day;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + diff);
      endOfWeek.setHours(23, 59, 59, 999);
      this.cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');
    }
    return of(true);
  }

}
